import React, {useCallback, useEffect, useState} from 'react';
import createAxiosInstance from '../../support/AxiosSupport';
import Dialog from '../Dialog';
import ConfirmDialog from '../ConfirmDialog';
import {CalendarIcon, RefreshIcon, ShieldIcon, TrashIcon} from '../icons';
import {formatDateTime} from '../../utils/dateTime';

/**
 * Управление интеграцией Google Calendar в Privacy Center (CTHS-174).
 *
 * До этой карточки подключиться из интерфейса было нельзя вовсе: Privacy Center показывал
 * «Не подключено» и не давал ни одной кнопки — OAuth-эндпоинты существовали только в API.
 *
 * <p>Порядок шагов продиктован GDPR, а не удобством: экран согласия показывается <b>до</b>
 * перехода к Google, потому что согласие на обработку данных календаря должно быть получено
 * до того, как данные станут доступны. Кнопка «Подключить» ведёт не к Google, а к объяснению.</p>
 *
 * <p>Redirect Google приходит на фронтовый маршрут `/calendar/callback`, а не на backend-эндпоинт:
 * тот требует JWT, которого у браузерного редиректа нет (см. `CalendarOAuthCallbackPage`).</p>
 */

interface CalendarStatus {
    connected: boolean;
    connectedAt: string | null;
}

interface CalendarSettings {
    selectedCalendarIds: string[];
    includeOnlineWithoutLocation: boolean;
    showOnHome: boolean;
    syncEnabled: boolean;
    windowPastDays: number;
    windowFutureDays: number;
    lastSyncedAt: string | null;
}

interface CalendarInfo {
    id: string;
    summary: string | null;
    description: string | null;
    backgroundColor: string | null;
    primary: boolean;
    selected: boolean;
}

type CardState = 'loading' | 'ready' | 'error';

const GoogleCalendarCard: React.FC = () => {
    const [state, setState] = useState<CardState>('loading');
    const [status, setStatus] = useState<CalendarStatus>({connected: false, connectedAt: null});
    const [settings, setSettings] = useState<CalendarSettings | null>(null);
    const [calendars, setCalendars] = useState<CalendarInfo[]>([]);
    const [calendarsFailed, setCalendarsFailed] = useState(false);
    const [consentOpen, setConsentOpen] = useState(false);
    const [disconnectOpen, setDisconnectOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);

    const load = useCallback(async () => {
        setState('loading');
        try {
            const api = await createAxiosInstance();
            const current = await api.get<CalendarStatus>('/calendar/oauth/status').then(r => r.data);
            setStatus(current);
            if (current.connected) {
                // Настройки обязательны, список календарей — нет: Google может быть недоступен,
                // и это не повод прятать подключение целиком.
                const [loadedSettings, loadedCalendars] = await Promise.all([
                    api.get<CalendarSettings>('/calendar/settings').then(r => r.data).catch(() => null),
                    api.get<CalendarInfo[]>('/calendar/calendars').then(r => r.data).catch(() => null),
                ]);
                setSettings(loadedSettings);
                setCalendars(loadedCalendars ?? []);
                setCalendarsFailed(loadedCalendars === null);
            } else {
                setSettings(null);
                setCalendars([]);
                setCalendarsFailed(false);
            }
            setState('ready');
        } catch {
            setState('error');
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const connect = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const api = await createAxiosInstance();
            const {data} = await api.get<{ authorizationUrl: string }>('/calendar/oauth/authorize');
            setConsentOpen(false);
            window.location.assign(data.authorizationUrl);
        } catch {
            setMessage('Не удалось начать подключение. Попробуйте позже.');
            setBusy(false);
        }
    };

    const patchSettings = async (patch: Partial<CalendarSettings>) => {
        setBusy(true);
        setMessage(null);
        try {
            const api = await createAxiosInstance();
            const {data} = await api.put<CalendarSettings>('/calendar/settings', patch);
            setSettings(data);
        } catch {
            setMessage('Не удалось сохранить настройки.');
        } finally {
            setBusy(false);
        }
    };

    const toggleCalendar = (id: string) => {
        const current = settings?.selectedCalendarIds ?? [];
        const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
        patchSettings({selectedCalendarIds: next});
    };

    const syncNow = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const api = await createAxiosInstance();
            const {data} = await api.post<{ skipped: boolean; created: number; updated: number }>(
                '/calendar/sync', {});
            setMessage(data.skipped
                ? 'События уже актуальны.'
                : `Синхронизировано: новых ${data.created}, обновлено ${data.updated}.`);
            await load();
        } catch {
            setMessage('Синхронизация не удалась. Попробуйте позже.');
        } finally {
            setBusy(false);
        }
    };

    const disconnect = async () => {
        setBusy(true);
        setMessage(null);
        try {
            const api = await createAxiosInstance();
            await api.delete('/calendar/oauth/revoke');
            setStatus({connected: false, connectedAt: null});
            setSettings(null);
            setCalendars([]);
            setDisconnectOpen(false);
        } catch {
            setMessage('Не удалось отключить календарь. Попробуйте позже.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="py-3" data-testid="integration-calendar">
            <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                    <span className="text-gray-500 dark:text-gray-400 shrink-0"><CalendarIcon size={20}/></span>
                    <div className="min-w-0">
                        <p className="text-gray-900 dark:text-gray-100">Google Calendar</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="calendar-status">
                            {state === 'loading' && 'Проверяем подключение…'}
                            {state === 'error' && 'Статус недоступен'}
                            {state === 'ready' && (status.connected
                                ? `Подключено ${status.connectedAt ? formatDateTime(status.connectedAt) : ''}`.trim()
                                : 'Не подключено')}
                        </p>
                    </div>
                </div>

                {state === 'error' && (
                    <button type="button" onClick={load} className="btn-secondary min-h-[44px] shrink-0"
                        data-testid="calendar-retry">
                        Повторить
                    </button>
                )}
                {state === 'ready' && !status.connected && (
                    <button
                        type="button"
                        onClick={() => setConsentOpen(true)}
                        className="btn-accent min-h-[44px] shrink-0"
                        data-testid="calendar-connect"
                    >
                        Подключить
                    </button>
                )}
                {state === 'ready' && status.connected && (
                    <button
                        type="button"
                        onClick={() => setDisconnectOpen(true)}
                        disabled={busy}
                        className="btn-secondary min-h-[44px] shrink-0"
                        data-testid="integration-calendar-disconnect"
                    >
                        <TrashIcon size={18}/>
                        Отключить
                    </button>
                )}
            </div>

            {state === 'ready' && status.connected && settings && (
                <div className="mt-4 space-y-4" data-testid="calendar-settings">
                    <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                            Какие календари синхронизировать
                        </p>
                        {calendarsFailed && (
                            <p className="text-sm text-gray-500 dark:text-gray-400"
                                data-testid="calendar-list-error">
                                Список календарей сейчас недоступен — синхронизируется основной.
                            </p>
                        )}
                        {!calendarsFailed && calendars.length === 0 && (
                            <p className="text-sm text-gray-500 dark:text-gray-400"
                                data-testid="calendar-list-empty">
                                В аккаунте Google нет доступных календарей.
                            </p>
                        )}
                        {calendars.length > 0 && (
                            <ul className="space-y-1">
                                {calendars.map(calendar => (
                                    <li key={calendar.id}>
                                        <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={settings.selectedCalendarIds.length === 0
                                                    ? calendar.primary
                                                    : settings.selectedCalendarIds.includes(calendar.id)}
                                                onChange={() => toggleCalendar(calendar.id)}
                                                disabled={busy}
                                                className="h-5 w-5 rounded border-gray-300 dark:border-white/20"
                                                data-testid={`calendar-option-${calendar.id}`}
                                            />
                                            <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                                                {calendar.summary || calendar.id}
                                                {calendar.primary && (
                                                    <span className="badge badge-neutral ml-2">основной</span>
                                                )}
                                            </span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <SettingToggle
                        label="Учитывать онлайн-встречи без места"
                        hint="Созвоны без адреса влияют только на верх образа."
                        checked={settings.includeOnlineWithoutLocation}
                        disabled={busy}
                        testid="calendar-toggle-online"
                        onChange={v => patchSettings({includeOnlineWithoutLocation: v})}
                    />
                    <SettingToggle
                        label="Показывать события на главной"
                        hint="Блок «Сегодня и скоро» под образом дня."
                        checked={settings.showOnHome}
                        disabled={busy}
                        testid="calendar-toggle-home"
                        onChange={v => patchSettings({showOnHome: v})}
                    />
                    <SettingToggle
                        label="Фоновая синхронизация"
                        hint="События подтягиваются автоматически раз в час."
                        checked={settings.syncEnabled}
                        disabled={busy}
                        testid="calendar-toggle-sync"
                        onChange={v => patchSettings({syncEnabled: v})}
                    />

                    <div className="flex flex-wrap items-center gap-3">
                        <button
                            type="button"
                            onClick={syncNow}
                            disabled={busy}
                            className="btn-secondary min-h-[44px]"
                            data-testid="calendar-sync-now"
                        >
                            <RefreshIcon size={18}/>
                            Синхронизировать сейчас
                        </button>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                            {settings.lastSyncedAt
                                ? `Последняя синхронизация: ${formatDateTime(settings.lastSyncedAt)}`
                                : 'Ещё не синхронизировалось'}
                        </span>
                    </div>
                </div>
            )}

            {message && (
                <p className="mt-3 text-sm text-gray-600 dark:text-gray-300" data-testid="calendar-message">
                    {message}
                </p>
            )}

            <Dialog
                open={consentOpen}
                onClose={() => setConsentOpen(false)}
                title="Подключение Google Calendar"
                busy={busy}
                data-testid="calendar-consent-dialog"
                footer={
                    <>
                        <button type="button" onClick={() => setConsentOpen(false)} disabled={busy}
                            className="btn-secondary min-h-[44px]" data-testid="calendar-consent-cancel">
                            Отмена
                        </button>
                        <button type="button" onClick={connect} disabled={busy}
                            className="btn-accent min-h-[44px]" data-testid="calendar-consent-accept">
                            {busy ? 'Открываем Google…' : 'Согласен, подключить'}
                        </button>
                    </>
                }
            >
                <div className="space-y-3 text-sm text-gray-700 dark:text-gray-300">
                    <p className="flex items-start gap-2">
                        <ShieldIcon size={18} className="shrink-0 mt-0.5 text-gray-500 dark:text-gray-400"/>
                        <span>
                            Приложение запросит <strong>только чтение</strong> календаря. Изменять
                            и создавать события оно не может.
                        </span>
                    </p>
                    <p>
                        <strong>Зачем.</strong> Тип события (деловая встреча, тренировка, ужин)
                        определяет, каким должен быть образ дня. Без календаря контекст приходится
                        вводить руками каждое утро.
                    </p>
                    <p>
                        <strong>Что хранится.</strong> Название, время, место и определённый тип
                        событий на ближайшие две недели. Описания, участники и вложения не
                        сохраняются. Названия и места хранятся в зашифрованном виде.
                    </p>
                    <p>
                        <strong>Как отключить.</strong> Кнопкой «Отключить» здесь же: доступ будет
                        отозван у Google, а сохранённые события удалены.
                    </p>
                </div>
            </Dialog>

            <ConfirmDialog
                open={disconnectOpen}
                title="Отключить Google Calendar?"
                description={
                    <span>
                        Синхронизация остановится, доступ будет отозван у Google, а все
                        сохранённые события удалятся. Подключить календарь снова можно в любой момент.
                    </span>
                }
                confirmLabel="Отключить и удалить события"
                destructive
                busy={busy}
                onConfirm={disconnect}
                onCancel={() => setDisconnectOpen(false)}
                data-testid="calendar-disconnect-dialog"
            />
        </div>
    );
};

/** Строка-тумблер настройки: подпись, пояснение и чекбокс с тач-зоной ≥ 44px. */
const SettingToggle: React.FC<{
    label: string;
    hint: string;
    checked: boolean;
    disabled: boolean;
    testid: string;
    onChange: (value: boolean) => void;
}> = ({label, hint, checked, disabled, testid, onChange}) => (
    <label className="flex items-start justify-between gap-4 min-h-[44px] cursor-pointer">
        <span className="min-w-0">
            <span className="block text-sm text-gray-900 dark:text-gray-100">{label}</span>
            <span className="block text-sm text-gray-500 dark:text-gray-400">{hint}</span>
        </span>
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={e => onChange(e.target.checked)}
            className="h-5 w-5 mt-0.5 shrink-0 rounded border-gray-300 dark:border-white/20"
            data-testid={testid}
        />
    </label>
);

export default GoogleCalendarCard;
